import { useState, useEffect } from 'react';
import { Ship, Anchor, Users, Crosshair, Lock } from 'lucide-react';
import { useBackgrounds } from '../context/BackgroundContext';
import { useAuth } from '../context/AuthContext';
import { useMember } from '../context/MemberContext';
import axios from 'axios';
import { MemberVesselRegister } from '../components/MemberVesselRegister';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default fleet data for display when no data exists
const defaultVessels = [
    { id: 'default-1', name: 'UEE Voidfang', ship_class: 'Carrier', role: 'Fleet Command', manufacturer: 'Aegis', crew_size: '80', status: 'active', squadron: 'Command' },
    { id: 'default-2', name: 'Nighthowl', ship_class: 'Frigate', role: 'Strike Lead', manufacturer: 'RSI', crew_size: '12', status: 'active', squadron: 'Alpha' },
    { id: 'default-3', name: 'Silent Hunter', ship_class: 'Capital', role: 'Heavy Assault', manufacturer: 'Drake', crew_size: '45', status: 'active', squadron: 'Command' },
    { id: 'default-4', name: 'Wolfpack Prime', ship_class: 'Destroyer', role: 'Escort', manufacturer: 'Aegis', crew_size: '24', status: 'active', squadron: 'Bravo' },
    { id: 'default-5', name: 'Shadow Claw', ship_class: 'Corvette', role: 'Recon', manufacturer: 'Origin', crew_size: '8', status: 'active', squadron: 'Recon' },
    { id: 'default-6', name: 'Iron Maw', ship_class: 'Frigate', role: 'Combat Support', manufacturer: 'Anvil', crew_size: '15', status: 'active', squadron: 'Alpha' },
];

const defaultSquadrons = [
    { id: 'default-1', name: 'Wolfpack Command', designation: 'CMD-01', role: 'Fleet Command & Coordination', commander: 'Admiral Vex', ship_count: 3 },
    { id: 'default-2', name: 'Alpha Strike', designation: 'ALPHA-01', role: 'Primary Strike Force', commander: 'Captain Nova', ship_count: 8 },
    { id: 'default-3', name: 'Bravo Heavy', designation: 'BRAVO-01', role: 'Heavy Assault & Siege', commander: 'Commander Steele', ship_count: 6 },
    { id: 'default-4', name: 'Ghost Recon', designation: 'RECON-01', role: 'Intelligence & Scouting', commander: 'Lieutenant Shadow', ship_count: 4 },
];

export const Hangar = () => {
    const { getBackground } = useBackgrounds();
    const { admin, token: adminToken } = useAuth();
    const { member, token: memberToken } = useMember();
    const [vessels, setVessels] = useState([]);
    const [squadrons, setSquadrons] = useState([]);
    const [activeTab, setActiveTab] = useState('command');
    const [loading, setLoading] = useState(true);
    
    const hangarBg = getBackground('hangar');
    const isLoggedIn = admin || member;

    const [shipClasses, setShipClasses] = useState([]);

    useEffect(() => {
        fetchData();
    }, [adminToken, memberToken]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Use auth token if available (admin or member)
            const authToken = adminToken || memberToken;
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
            
            const [vesselsRes, squadronsRes, shipClassesRes] = await Promise.all([
                axios.get(`${API}/vessels`, { headers }),
                axios.get(`${API}/squadrons`),
                axios.get(`${API}/ship-classes`)
            ]);
            
            // Use database data if available, otherwise use defaults
            setVessels(vesselsRes.data.length > 0 ? vesselsRes.data : defaultVessels);
            setSquadrons(squadronsRes.data.length > 0 ? squadronsRes.data : defaultSquadrons);
            setShipClasses(shipClassesRes.data);
        } catch (error) {
            console.error('Failed to fetch fleet data:', error);
            // Use defaults on error
            setVessels(defaultVessels);
            setSquadrons(defaultSquadrons);
        } finally {
            setLoading(false);
        }
    };

    // Get command class names from ship classes registry
    const commandClassNames = shipClasses
        .filter(sc => sc.vessel_type === 'command')
        .map(sc => sc.name.toLowerCase());
    
    // Filter vessels based on ship class type from registry
    const capitalShips = vessels.filter(v => {
        const shipClassName = (v.ship_class || '').toLowerCase();
        return commandClassNames.some(cmdClass => shipClassName.includes(cmdClass.toLowerCase()));
    });
    
    // Ships that don't fall into command category
    const supportShips = vessels.filter(v => {
        const shipClassName = (v.ship_class || '').toLowerCase();
        return !commandClassNames.some(cmdClass => shipClassName.includes(cmdClass.toLowerCase()));
    });

    return (
        <div className="min-h-screen bg-[#050505] pt-16" data-testid="hangar-page">
            {/* Hero */}
            <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${hangarBg})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
                <div className="relative z-10 text-center px-4">
                    <span className="font-mono text-xs text-red-500 tracking-[0.3em]">FLEET REGISTRY</span>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase mt-2">
                        The Hangar
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-4">
                        Command Vessels • Carriers • Strike Squadrons
                    </p>
                </div>
            </section>

            {/* Tabs */}
            <section className="px-4 py-8">
                <div className="max-w-6xl mx-auto">
                    <MemberVesselRegister shipClasses={shipClasses} />
                    <div className="flex gap-4 mb-8 border-b border-white/10">
                        <button
                            onClick={() => setActiveTab('command')}
                            className={`pb-4 px-6 font-mono text-sm uppercase tracking-wider transition-colors ${
                                activeTab === 'command' 
                                    ? 'text-red-500 border-b-2 border-red-500' 
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                            data-testid="tab-command"
                        >
                            <Anchor className="w-4 h-4 inline mr-2" />
                            Command Vessels
                        </button>
                        <button
                            onClick={() => setActiveTab('vessels')}
                            className={`pb-4 px-6 font-mono text-sm uppercase tracking-wider transition-colors ${
                                activeTab === 'vessels' 
                                    ? 'text-red-500 border-b-2 border-red-500' 
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                            data-testid="tab-vessels"
                        >
                            <Ship className="w-4 h-4 inline mr-2" />
                            Fleet Vessels
                        </button>
                        <button
                            onClick={() => setActiveTab('squadrons')}
                            className={`pb-4 px-6 font-mono text-sm uppercase tracking-wider transition-colors ${
                                activeTab === 'squadrons' 
                                    ? 'text-red-500 border-b-2 border-red-500' 
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                            data-testid="tab-squadrons"
                        >
                            <Crosshair className="w-4 h-4 inline mr-2" />
                            Squadrons
                        </button>
                    </div>

                    {/* Command Vessels Tab */}
                    {activeTab === 'command' && (
                        <div data-testid="command-content">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {capitalShips.map((vessel) => (
                                    <div key={vessel.id} className={`vessel-card ${vessel.visibility === 'private' ? 'border-l-4 border-purple-500/50' : ''}`} data-testid={`vessel-${vessel.id}`}>
                                        <div className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-mono text-[10px] text-gray-500 tracking-wider">{vessel.ship_class.toUpperCase()}</span>
                                                        {vessel.visibility === 'private' && (
                                                            <span className="badge bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] flex items-center gap-1">
                                                                <Lock className="w-3 h-3" /> Classified
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="font-heading font-bold text-lg text-white">{vessel.name}</h3>
                                                </div>
                                                <span className={`badge ${vessel.status === 'active' ? 'badge-active' : 'badge-pending'}`}>
                                                    {vessel.status}
                                                </span>
                                            </div>
                                            <div className="space-y-2 font-mono text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Role</span>
                                                    <span className="text-gray-300">{vessel.role}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Manufacturer</span>
                                                    <span className="text-gray-300">{vessel.manufacturer}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Crew</span>
                                                    <span className="text-gray-300">{vessel.crew_size}</span>
                                                </div>
                                                {vessel.squadron && (
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">Squadron</span>
                                                        <span className="text-red-500">{vessel.squadron}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {capitalShips.length === 0 && (
                                <div className="text-center py-12">
                                    <Anchor className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No command vessels in fleet registry</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Fleet Vessels Tab */}
                    {activeTab === 'vessels' && (
                        <div data-testid="vessels-content">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {supportShips.map((vessel) => (
                                    <div key={vessel.id} className={`vessel-card ${vessel.visibility === 'private' ? 'border-l-4 border-purple-500/50' : ''}`} data-testid={`vessel-${vessel.id}`}>
                                        <div className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-mono text-[10px] text-gray-500 tracking-wider">{vessel.ship_class.toUpperCase()}</span>
                                                        {vessel.visibility === 'private' && (
                                                            <span className="badge bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] flex items-center gap-1">
                                                                <Lock className="w-3 h-3" /> Classified
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="font-heading font-bold text-lg text-white">{vessel.name}</h3>
                                                </div>
                                                <span className={`badge ${vessel.status === 'active' ? 'badge-active' : 'badge-pending'}`}>
                                                    {vessel.status}
                                                </span>
                                            </div>
                                            <div className="space-y-2 font-mono text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Role</span>
                                                    <span className="text-gray-300">{vessel.role}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Manufacturer</span>
                                                    <span className="text-gray-300">{vessel.manufacturer}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Crew</span>
                                                    <span className="text-gray-300">{vessel.crew_size}</span>
                                                </div>
                                                {vessel.squadron && (
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">Squadron</span>
                                                        <span className="text-red-500">{vessel.squadron}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {supportShips.length === 0 && (
                                <div className="text-center py-12">
                                    <Ship className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No fleet vessels in registry</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Squadrons Tab */}
                    {activeTab === 'squadrons' && (
                        <div data-testid="squadrons-content">
                            <div className="grid md:grid-cols-2 gap-6">
                                {squadrons.map((squadron) => (
                                    <div key={squadron.id} className="tactical-panel p-6" data-testid={`squadron-${squadron.id}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <span className="font-mono text-xs text-red-500 tracking-wider">{squadron.designation}</span>
                                                <h3 className="font-heading font-bold text-xl text-white mt-1">{squadron.name}</h3>
                                            </div>
                                            <div className="bg-red-500/10 border border-red-500/30 px-3 py-1">
                                                <span className="font-mono text-sm text-red-500">{squadron.ship_count} Ships</span>
                                            </div>
                                        </div>
                                        <p className="font-body text-gray-400 text-sm mb-4">{squadron.role}</p>
                                        <div className="flex items-center gap-2">
                                            <Users className="w-4 h-4 text-gray-500" />
                                            <span className="font-mono text-xs text-gray-500">Commander:</span>
                                            <span className="font-mono text-sm text-white">{squadron.commander}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {squadrons.length === 0 && (
                                <div className="text-center py-12">
                                    <Crosshair className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No squadrons in registry</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Fleet Stats - Always visible */}
                    <div className="tactical-panel p-8 mt-8">
                        <h3 className="font-heading font-bold text-lg text-white uppercase mb-6">Fleet Overview</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="font-mono text-3xl font-bold text-white">{vessels.length}</div>
                                <div className="font-mono text-xs text-gray-500 tracking-wider mt-1">TOTAL VESSELS</div>
                            </div>
                            <div className="text-center">
                                <div className="font-mono text-3xl font-bold text-red-500">{capitalShips.length}</div>
                                <div className="font-mono text-xs text-gray-500 tracking-wider mt-1">COMMAND CLASS</div>
                            </div>
                            <div className="text-center">
                                <div className="font-mono text-3xl font-bold text-white">{squadrons.length}</div>
                                <div className="font-mono text-xs text-gray-500 tracking-wider mt-1">SQUADRONS</div>
                            </div>
                            <div className="text-center">
                                <div className="font-mono text-3xl font-bold text-green-500">
                                    {vessels.filter(v => v.status === 'active').length}
                                </div>
                                <div className="font-mono text-xs text-gray-500 tracking-wider mt-1">COMBAT READY</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
